// Phone drag engine — reorder, place-from-tray and height resize.
//
// How a drag works:
//  1. LIFT — long-press a module's header (a normal swipe still scrolls the
//     page), or press its ⋮⋮ grip, or (mouse) just drag. A lightweight stand-in
//     card lifts — the module's header + its skeleton outline (the same one it
//     shows while loading) — and follows the finger via `transform` only. The
//     real module stays hidden where it was, untouched, so nothing inside it
//     re-lays out or redraws during the drag.
//  2. AIM — at lift, every possible landing (./layout.ts dropOptions + swapOptions) is laid
//     out once with the same geometry the page renders. While dragging, what
//     the card's centre is over ON THE BOARD AS IT WAS AT LIFT picks the
//     option (./layout.ts aimRows), so targets never slide away under the
//     finger as the preview rearranges. Only when the winner changes do the
//     other modules glide (CSS transitions on left/top/width/height) and the
//     green ghost slot move. Nothing React re-renders mid-drag.
//  3. DROP — the winning arrangement is committed to the store once; React
//     re-renders to exactly the positions already on screen, the card glides
//     into its slot, and the real module (moved there once) fades up in its
//     place.
// Scrolling is blocked only while a module is lifted (touchmove
// preventDefault), and the page auto-scrolls near the top / bottom edge.
//
// Placing from the tray reuses the same machinery: the new module appears as a
// lifted card in the middle of the screen with the ghost showing where it
// would land; scroll or drag to aim, tap / release to place, × to cancel.

import { useBentoStore } from "../store/index.js";
import {
  aimRows,
  buildRows,
  clampH,
  H_MIN,
  dropOptions,
  layoutRows,
  rowsToArrangement,
  sameRows,
  swapOptions,
  type Metrics,
  type Rect,
  type Row,
} from "./layout.js";
import { layoutItems, mobileHeight, readMetrics } from "./model.js";
import { skeletonHTML, skeletonKindFor } from "../components/widgets/skeletonShapes.js";

const HOLD_MS = 250; // long-press to lift (touch)
const HOLD_SLOP = 12; // px a finger may wander before a hold becomes a scroll
const LAND_MS = 380; // the card's glide into its slot (.m-proxy.m-dropping)
const LIFT_SCALE = 0.5; // a lifted card shrinks to half its size
const MOUSE_SLOP = 4; // px a mouse must move before a drag starts
const EDGE = 90; // px from the top / bottom edge that auto-scrolls
const SCROLL_MAX = 16; // px per frame at the very edge
const TOP_BAR = 50;
const TRAY = 48;

interface Option {
  rows: Row[];
  rects: Map<string, Rect>;
  height: number;
  slot: Rect;
}

interface Session {
  id: string;
  el: HTMLElement; // the real module — hidden in place (.m-origin) while lifted
  card: HTMLElement; // the stand-in that's dragged (.m-proxy)
  mode: "drag" | "place";
  pointerId: number | null; // null = placing, no finger down yet
  fingerX: number; // client coords (or the virtual finger while placing)
  fingerY: number;
  grabX: number; // finger offset inside the card (px, at its resting size)
  grabY: number;
  rest: Rect; // the card's resting rect (workspace coords) — fixed during the drag
  // The card's shape while lifted: the size of the slot it's aiming at, so it
  // turns half or full width as the landing does. Aiming still uses the
  // resting size, so a shape change can't move the aim point and flip-flop
  // between two landings.
  shape: { w: number; h: number };
  // Every lifted card shrinks to half its size (LIFT_SCALE), whatever its
  // width — the same rule every time, and small enough to aim at either half
  // of a row; a card being placed from the tray starts at that size.
  // scale eases toward scaleTarget.
  scale: number;
  scaleTarget: number;
  options: Option[];
  // The board as it was at lift — what aiming is judged against (./layout.ts aimRows).
  all: Row[];
  liftRects: Map<string, Rect>;
  small: Map<string, boolean>;
  current: number;
  original: number; // the option equal to the starting arrangement (cancel → here)
  ghost: HTMLElement;
  moved: boolean;
  downX: number;
  downY: number;
  raf: number;
}

let s: Session | null = null;
let hold: {
  el: HTMLElement;
  id: string;
  pointerId: number;
  pointerType: string;
  x: number;
  y: number;
  timer: number;
} | null = null;
let resize: { id: string; startY: number; startH: number; unit: number; h: number } | null = null;
// The last drop's landing: once the card has glided into its slot, it's
// removed and the real module shown. Settled early if another lift starts.
let landing: { el: HTMLElement; card: HTMLElement; timer: number } | null = null;

function settleLanding() {
  if (!landing) return;
  const { el, card, timer } = landing;
  landing = null;
  window.clearTimeout(timer);
  card.remove();
  el.classList.remove("m-origin");
  el.classList.add("m-arrived");
  window.setTimeout(() => el.classList.remove("m-arrived"), 300);
}

/** The stand-in card: the module's header (a static copy) + its skeleton. */
function makeCard(el: HTMLElement, id: string, rest: Rect): HTMLElement {
  const card = document.createElement("div");
  card.className = "m-proxy";
  const header = el.querySelector(".module-header");
  if (header) card.appendChild(header.cloneNode(true));
  const title = useBentoStore.getState().modules.find((m) => m.id === id)?.title;
  const kind = skeletonKindFor(title);
  const body = document.createElement("div");
  body.className = "module-body";
  body.innerHTML = `<div class="data-wrapper top-align bm-skeleton skel-${kind}">${skeletonHTML(kind)}</div>`;
  card.appendChild(body);
  setRect(card, rest);
  ws().appendChild(card);
  return card;
}

const ws = () => document.getElementById("workspace") as HTMLElement;
const moduleEl = (id: string) =>
  ws().querySelector<HTMLElement>(`.placed-module[data-module-id="${id}"]`);

function toWs(cx: number, cy: number) {
  const r = ws().getBoundingClientRect();
  return { x: cx - r.left, y: cy - r.top };
}

function setRect(el: HTMLElement, r: Rect) {
  el.style.left = r.x + "px";
  el.style.top = r.y + "px";
  el.style.width = r.w + "px";
  el.style.height = r.h + "px";
}

// ── Hold → lift ──────────────────────────────────────────────────────────────

/** Module header pointerdown (phone). Ignores the header's action buttons. */
export function startHold(el: HTMLElement, id: string, e: PointerEvent): void {
  if (s || resize || hold || e.button > 0) return;
  const target = e.target as Element;
  if (target.closest(".header-actions")) return;
  const onGrip = !!target.closest(".mobile-drag-handle");
  if (onGrip && e.pointerType !== "mouse") {
    e.preventDefault();
    lift(el, id, e.pointerId, e.clientX, e.clientY);
    return;
  }
  hold = {
    el,
    id,
    pointerId: e.pointerId,
    pointerType: e.pointerType,
    x: e.clientX,
    y: e.clientY,
    timer: 0,
  };
  if (e.pointerType !== "mouse") {
    el.classList.add("m-pressing");
    hold.timer = window.setTimeout(() => {
      if (!hold) return;
      const h = hold;
      lift(h.el, h.id, h.pointerId, h.x, h.y);
    }, HOLD_MS);
  }
}

function clearHold() {
  if (!hold) return;
  window.clearTimeout(hold.timer);
  hold.el.classList.remove("m-pressing");
  hold = null;
}

function lift(el: HTMLElement, id: string, pointerId: number | null, cx: number, cy: number) {
  clearHold();
  settleLanding();
  const modules = useBentoStore.getState().modules;
  const items = layoutItems(modules);
  const small = new Map(items.map((it) => [it.id, it.small]));
  const height = new Map(items.map((it) => [it.id, it.h]));
  const all = buildRows(items);
  const without = all.map((r) => r.filter((x) => x !== id)).filter((r) => r.length > 0);
  const m: Metrics = readMetrics(ws());
  const landings = dropOptions(without, id, !!small.get(id), (x) => !!small.get(x));
  for (const rows of swapOptions(all, id, !!small.get(id)))
    if (!landings.some((o) => sameRows(o, rows))) landings.push(rows);
  const options: Option[] = landings.map(
    (rows) => {
      const { rects, height: h } = layoutRows(rows, (x) => height.get(x) ?? H_MIN, m);
      return { rows, rects, height: h, slot: rects.get(id)! };
    }
  );
  let original = options.findIndex((o) => sameRows(o.rows, all));
  if (original < 0) original = 0;

  const wr = ws().getBoundingClientRect();
  const er = el.getBoundingClientRect();
  const rest = { x: er.left - wr.left, y: er.top - wr.top, w: er.width, h: er.height };
  const ghost = document.createElement("div");
  ghost.className = "m-slot";
  ws().appendChild(ghost);
  const card = makeCard(el, id, rest);

  s = {
    id,
    el,
    card,
    mode: pointerId === null ? "place" : "drag",
    pointerId,
    fingerX: cx,
    fingerY: cy,
    grabX: cx - er.left,
    grabY: cy - er.top,
    rest,
    shape: { w: rest.w, h: rest.h },
    scale: pointerId === null ? LIFT_SCALE : 1,
    scaleTarget: LIFT_SCALE,
    options,
    all,
    liftRects: layoutRows(all, (x) => height.get(x) ?? H_MIN, m).rects,
    small,
    current: original,
    original,
    ghost,
    moved: false,
    downX: cx,
    downY: cy,
    raf: 0,
  };
  if (pointerId === null) {
    ghost.classList.add("m-slot-placing");
    ghost.textContent = "Tap to place here";
  }
  if (pointerId !== null) {
    try {
      el.setPointerCapture(pointerId);
    } catch {
      /* the pointer may already be gone */
    }
  }
  navigator.vibrate?.(10);
  el.classList.add("m-origin");
  card.style.transformOrigin = "0 0";
  applyOption(original, false);
  frame();
}

// ── Drag loop ────────────────────────────────────────────────────────────────

// The card scaled about its top-left, the finger kept on the same relative
// point of it: at its resting size for aiming, or (drawn) at its lifted shape.
function cardRect(drawn = false): Rect {
  const p = toWs(s!.fingerX, s!.fingerY);
  const k = s!.scale;
  const w = drawn ? s!.shape.w : s!.rest.w;
  const h = drawn ? s!.shape.h : s!.rest.h;
  const fx = s!.grabX / s!.rest.w;
  const fy = s!.grabY / s!.rest.h;
  return { x: p.x - fx * w * k, y: p.y - fy * h * k, w: w * k, h: h * k };
}

function frame() {
  if (!s) return;
  s.raf = 0;
  // Auto-scroll near the edges (only while a finger is driving the card).
  let speed = 0;
  if (s.pointerId !== null) {
    const top = TOP_BAR + EDGE;
    const bottom = window.innerHeight - TRAY - EDGE;
    if (s.fingerY < top) speed = -SCROLL_MAX * Math.min(1, (top - s.fingerY) / EDGE);
    else if (s.fingerY > bottom) speed = SCROLL_MAX * Math.min(1, (s.fingerY - bottom) / EDGE);
    if (speed) window.scrollBy(0, speed);
  }
  // Ease the lift-time shrink (a few frames).
  const shrinking = Math.abs(s.scale - s.scaleTarget) > 0.002;
  s.scale = shrinking ? s.scale + (s.scaleTarget - s.scale) * 0.3 : s.scaleTarget;
  const d = cardRect(true);
  s.card.style.transform = `translate3d(${d.x - s.rest.x}px, ${d.y - s.rest.y}px, 0) scale(${s.scale * 1.06})`;
  s.el.classList.add("m-origin"); // in case a re-render rewrote className
  pickOption(cardRect());
  if (speed || shrinking) s.raf = requestAnimationFrame(frame);
}

function schedule() {
  if (s && !s.raf) s.raf = requestAnimationFrame(frame);
}

function pickOption(card: Rect) {
  if (!s) return;
  const rows = aimRows(s.all, s.liftRects, s.id, s.small.get(s.id) ?? false,
    (x) => s!.small.get(x) ?? false, card.x + card.w / 2, card.y + card.h / 2);
  const i = s.options.findIndex((o) => sameRows(o.rows, rows));
  if (i >= 0 && i !== s.current) applyOption(i, true);
}

function applyOption(i: number, animate: boolean) {
  if (!s) return;
  s.current = i;
  const o = s.options[i]!;
  ws().querySelectorAll<HTMLElement>(".placed-module").forEach((el) => {
    const id = el.dataset.moduleId;
    if (!id || id === s!.id) return;
    const r = o.rects.get(id);
    if (r) setRect(el, r);
  });
  s.ghost.classList.toggle("m-slot-instant", !animate);
  setRect(s.ghost, o.slot);
  // The card takes the landing's shape (its width / height transition — cheap:
  // it only holds a header and a skeleton).
  if (s.shape.w !== o.slot.w || s.shape.h !== o.slot.h) {
    s.shape = { w: o.slot.w, h: o.slot.h };
    s.card.style.width = o.slot.w + "px";
    s.card.style.height = o.slot.h + "px";
    schedule();
  }
  ws().style.setProperty("--m-ws-h", o.height + "px");
}

// ── Drop / cancel ────────────────────────────────────────────────────────────

function finish(commit: boolean) {
  if (!s) return;
  const sess = s;
  s = null;
  if (sess.raf) cancelAnimationFrame(sess.raf);
  if (!commit) applyOptionFor(sess, sess.original);
  const o = sess.options[commit ? sess.current : sess.original]!;
  // The real module (still hidden) moves to its slot in one step — its one
  // re-layout of the drag. The card glides there: its resting rect moves and
  // the transform drops, together (both transition). Then it hands over.
  setRect(sess.el, o.slot);
  sess.card.classList.add("m-dropping");
  setRect(sess.card, o.slot);
  sess.card.style.transform = "";
  sess.ghost.remove();
  ws().style.setProperty("--m-ws-h", o.height + "px");
  useBentoStore.getState().arrangeMobile(rowsToArrangement(o.rows));
  settleLanding();
  landing = { el: sess.el, card: sess.card, timer: window.setTimeout(settleLanding, LAND_MS) };
  setTray(false);
}

function applyOptionFor(sess: Session, i: number) {
  const prev = s;
  s = sess;
  applyOption(i, true);
  s = prev;
}

// ── Document listeners ───────────────────────────────────────────────────────

function onMove(e: PointerEvent) {
  if (resize) {
    // Snap to the nearest allowed height (./layout.ts H_LEVELS).
    const h = clampH(resize.startH + (e.clientY - resize.startY) / resize.unit);
    if (h !== resize.h) {
      resize.h = h;
      useBentoStore.getState().setMobileUnits([resize.id], h);
    }
    return;
  }
  if (hold && e.pointerId === hold.pointerId) {
    const d = Math.hypot(e.clientX - hold.x, e.clientY - hold.y);
    if (hold.pointerType === "mouse") {
      if (d >= MOUSE_SLOP) {
        const h = hold;
        lift(h.el, h.id, h.pointerId, h.x, h.y);
        s!.fingerX = e.clientX;
        s!.fingerY = e.clientY;
        schedule();
      }
    } else if (d > HOLD_SLOP) {
      clearHold(); // it's a scroll, not a hold
    }
    return;
  }
  if (!s || s.pointerId !== e.pointerId) return;
  s.fingerX = e.clientX;
  s.fingerY = e.clientY;
  if (Math.hypot(e.clientX - s.downX, e.clientY - s.downY) > HOLD_SLOP) s.moved = true;
  schedule();
}

function onUp(e: PointerEvent) {
  if (ghostTap && ghostTap.pointerId === e.pointerId) {
    const tap = Math.hypot(e.clientX - ghostTap.x, e.clientY - ghostTap.y) < HOLD_SLOP;
    ghostTap = null;
    if (tap && s && s.mode === "place" && s.pointerId === null) finish(true);
    return;
  }
  if (resize) {
    resize = null;
    return;
  }
  if (hold && e.pointerId === hold.pointerId) {
    clearHold();
    return;
  }
  if (s && s.pointerId === e.pointerId) finish(true);
}

function onCancel(e: PointerEvent) {
  if (resize) resize = null;
  if (hold && e.pointerId === hold.pointerId) clearHold();
  if (ghostTap && ghostTap.pointerId === e.pointerId) ghostTap = null; // it was a scroll
  if (s && s.pointerId === e.pointerId) {
    if (s.mode === "place") {
      s.pointerId = null; // keep placing; the card stays floating
      return;
    }
    finish(false);
  }
}

// While placing, a press on the floating card picks it up (drag to aim, or
// just tap to place where the ghost shows); a tap on the green ghost places it
// there too. (The ghost has pointer-events: none, so it's hit-tested here.)
let ghostTap: { pointerId: number; x: number; y: number } | null = null;
function onDown(e: PointerEvent) {
  if (!s || s.mode !== "place" || s.pointerId !== null) return;
  if (!(e.target as Element).closest(".m-proxy")) {
    const g = s.ghost.getBoundingClientRect();
    if (e.clientX >= g.left && e.clientX <= g.right && e.clientY >= g.top && e.clientY <= g.bottom)
      ghostTap = { pointerId: e.pointerId, x: e.clientX, y: e.clientY };
    return;
  }
  e.preventDefault();
  const c = cardRect(true);
  const p = toWs(e.clientX, e.clientY);
  s.pointerId = e.pointerId;
  s.grabX = ((p.x - c.x) / c.w) * s.rest.w;
  s.grabY = ((p.y - c.y) / c.h) * s.rest.h;
  s.fingerX = e.clientX;
  s.fingerY = e.clientY;
  s.downX = e.clientX;
  s.downY = e.clientY;
  s.moved = false;
  try {
    s.el.setPointerCapture(e.pointerId);
  } catch {
    /* ignore */
  }
}

let installed = false;
function install() {
  if (installed) return;
  installed = true;
  document.addEventListener("pointerdown", onDown, true);
  document.addEventListener("pointermove", onMove, { passive: true });
  document.addEventListener("pointerup", onUp);
  document.addEventListener("pointercancel", onCancel);
  // Block page scrolling only while a module is lifted / resizing (must be a
  // non-passive touchmove — pointer events can't stop a scroll).
  document.addEventListener(
    "touchmove",
    (e) => {
      if (resize || (s && s.pointerId !== null)) e.preventDefault();
    },
    { passive: false }
  );
  document.addEventListener("contextmenu", (e) => {
    if (hold || s) e.preventDefault(); // Android long-press menu
  });
  // Placing: the card floats in the middle of the screen while you scroll.
  window.addEventListener(
    "scroll",
    () => {
      if (s && s.mode === "place" && s.pointerId === null) {
        s.fingerX = window.innerWidth / 2;
        s.fingerY = (TOP_BAR + window.innerHeight - TRAY) / 2;
        schedule();
      }
    },
    { passive: true }
  );
}

// ── Placing from the tray ────────────────────────────────────────────────────

function setTray(placing: boolean) {
  const tray = document.getElementById("mobile-tray");
  const label = document.getElementById("mobile-tray-label");
  tray?.classList.toggle("placement-cancel", placing);
  if (label) label.textContent = placing ? "Cancel" : "+ Add Module";
}

/** A module just added from the tray: float it mid-screen and aim. */
export function startPlacing(id: string): void {
  install();
  if (s) finish(true);
  const el = moduleEl(id);
  if (!el) return;
  const er = el.getBoundingClientRect();
  const fx = window.innerWidth / 2;
  const fy = (TOP_BAR + window.innerHeight - TRAY) / 2;
  lift(el, id, null, er.left + er.width / 2, er.top + er.height / 2);
  if (!s) return;
  s.grabX = s.rest.w / 2;
  s.grabY = s.rest.h / 2;
  s.fingerX = fx;
  s.fingerY = fy;
  setTray(true);
  schedule();
}

export function isPlacing(): boolean {
  return !!s && s.mode === "place";
}

/** Tray × while placing: drop the new module altogether. */
export function cancelPlacing(): void {
  if (!s || s.mode !== "place") return;
  const sess = s;
  s = null;
  if (sess.raf) cancelAnimationFrame(sess.raf);
  applyOptionFor(sess, sess.original);
  sess.ghost.remove();
  sess.card.remove();
  setTray(false);
  useBentoStore.getState().removeModule(sess.id);
}

// ── Height resize (bottom-edge grip) ─────────────────────────────────────────

export function startResize(id: string, e: PointerEvent): void {
  if (s || hold) return;
  e.preventDefault();
  e.stopPropagation();
  try {
    (e.target as Element).setPointerCapture(e.pointerId);
  } catch {
    /* ignore */
  }
  const m = useBentoStore.getState().modules.find((x) => x.id === id);
  if (!m) return;
  const h = mobileHeight(m);
  resize = { id, startY: e.clientY, startH: h, unit: readMetrics(ws()).half, h };
}

// Module-header entry point (also used by main.js's startModuleGesture).
export function startModuleHold(el: HTMLElement, e: PointerEvent): void {
  install();
  const id = el.dataset.moduleId;
  if (id) startHold(el, id, e);
}

export function startModuleResize(el: HTMLElement, e: PointerEvent): void {
  install();
  const id = el.dataset.moduleId;
  if (id) startResize(id, e);
}
